import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import type { IFilterOption } from '@rankmi/rankmi-table/dist/interfaces/filterProps'

import useSWR from 'swr'
import { joinKeys } from 'helpers/translations.helper'
import { IMainState } from 'store/states'
import * as fromProfilesActions from 'store/actions/lms-profiles.action'
import {
  CourseLmsProfileType,
  ChildFilterTypes,
  EnrolState,
  getTranslationEnrolStateKey,
  getTranslationKeyCourseLmsProfile,
} from 'enum'
import {
  MonitoringStatus,
  getTranslationKeyMonitoringStatus,
} from 'enum/monitoring-status.enum'
import { IntegrationTypes } from 'enum/integrationTypes.enum'
import { ProviderType } from 'enum/providerType.enum'
import providersList from 'api/integrations/proversList'
import { ENROL_STATUS_MAP } from '../../../enum/monitoring-status.enum'
import { FiltersColumns } from '../constants'
import { ColumnAreaValue } from '../types'

interface Column extends IFilterOption {
  key: FiltersColumns
  label: string
  type: string
  value: string | ColumnAreaValue<ChildFilterTypes>
}

const prefix = 'monitoring.attributes'
const prefixFilters = joinKeys(prefix, 'profiles_list')

export const useFiltersColumns = () => {
  const dispatch = useDispatch()
  const { allProfilesList, loading: profilesLoading } = useSelector(
    ({ lmsProfiles }: IMainState) => lmsProfiles
  )
  const { t } = useTranslation()

  useEffect(() => {
    dispatch({ type: fromProfilesActions.LMS_ALL_PROFILES_LIST })
  }, [dispatch])

  const { data: providerData } = useSWR(
    { key: 'listIntegrationData' },
    async () => providersList()
  )

  const providerFilters = useMemo((): Column[] => {
    const integrationsActive = new Set(
      providerData?.map((integration) => integration.integrationType)
    )
    const integrationList = [
      {
        provider: ProviderType.Coursera,
        label: t(`${prefix}.providerList.coursera`),
        integration: IntegrationTypes.Coursera,
      },
      {
        provider: ProviderType.Udemy,
        label: t(`${prefix}.providerList.udemy`),
        integration: IntegrationTypes.Udemy,
      },
    ].filter(({ integration }) => integrationsActive.has(integration))

    if (integrationList.length) {
      const internalCourseValue = '-1'
      return [
        {
          key: FiltersColumns.provider,
          label: t(`${prefix}.provider`),
          value: {
            children: {
              [ChildFilterTypes.Course]: 'integration_course_provider_filter',
            },
          },
          type: 'list',
          list: [
            ...integrationList.map(({ provider, label }) => ({
              value: provider.toString(),
              label,
            })),
            {
              value: internalCourseValue,
              label: 'Interno',
            },
          ],
        },
      ]
    }

    return []
  }, [providerData, t])

  const parent = 'users_users_all_courses'
  const parentCourse = `${parent}_course`
  const filtersColumns = useMemo((): Column[] => {
    const filterProfiles = profilesLoading
      ? [
          {
            key: FiltersColumns.requiredProfile,
            label: t(`${prefix}.required_profile_course`),
            value: {
              children: {
                [ChildFilterTypes.Course]: 'required_profiles_id',
                [ChildFilterTypes.Participant]:
                  'users_all_courses_course_required_profiles_id',
                [ChildFilterTypes.ExternalCourse]: null,
                [ChildFilterTypes.Area]: `${parentCourse}_required_profiles_id`,
                [ChildFilterTypes.LearningPath]: 'profiles_id',
              },
            },
            type: 'tagsList',
            list: allProfilesList.map(
              ({ id: value, name: label, color }: any) => ({
                value,
                label,
                color,
              })
            ),
          },
          {
            key: FiltersColumns.optionalProfile,
            label: t(`${prefix}.optional_profile_course`),
            value: {
              children: {
                [ChildFilterTypes.Course]: 'optional_profiles_id',
                [ChildFilterTypes.Participant]:
                  'users_all_courses_course_optional_profiles_id',
                [ChildFilterTypes.Area]: `${parentCourse}_optional_profiles_id`,
                [ChildFilterTypes.LearningPath]: 'profiles_id',
              },
            },
            type: 'tagsList',
            list: allProfilesList.map(
              ({ id: value, name: label, color }: any) => ({
                value,
                label,
                color,
              })
            ),
          },
          {
            key: FiltersColumns.userProfile,
            label: t(`${prefix}.user_profile`),
            value: {
              children: {
                [ChildFilterTypes.Course]:
                  'users_all_courses_user_lms_profiles_id',
                [ChildFilterTypes.Participant]: 'lms_profiles_id',
                [ChildFilterTypes.Area]: `users_lms_profiles_id`,
                [ChildFilterTypes.LearningPath]: 'profiles_id',
              },
            },
            type: 'tagsList',
            list: allProfilesList.map(
              ({ id: value, name: label, color }: any) => ({
                value,
                label,
                color,
              })
            ),
          },
        ]
      : []

    const userStateFilter: Column = {
      key: FiltersColumns.userState,
      label: t(`${prefix}.userState`),
      value: {
        children: {
          [ChildFilterTypes.Course]: 'users_all_courses_user_active_filter',
          [ChildFilterTypes.ExternalCourse]: 'users_active_filter',
          [ChildFilterTypes.Participant]: 'active_filter',
          [ChildFilterTypes.Area]: 'users_active_filter',
          [ChildFilterTypes.LearningPath]: 'users_active_filter',
          [ChildFilterTypes.UsersAllCourse]: 'user_active_filter',
        },
      },
      type: 'tagsList',
      list: [true, false].map((value) => ({
        value: String(value),
        label: t(`${prefix}.userStateList.${value}`),
      })),
    }

    return [
      userStateFilter,
      {
        key: FiltersColumns.areaName,
        label: t(`${prefix}.name`),
        value: {
          children: {
            [ChildFilterTypes.Course]: 'users_all_courses_user_area_name',
            [ChildFilterTypes.ExternalCourse]: 'users_area_name',
            [ChildFilterTypes.Participant]: 'area_name',
            [ChildFilterTypes.Area]: 'name',
            [ChildFilterTypes.LearningPath]: 'name',
            [ChildFilterTypes.UsersAllCourse]: 'user_area_name',
          },
        },
        type: 'string',
      },
      {
        key: FiltersColumns.courseName,
        label: t(`${prefix}.course_name`),
        value: {
          children: {
            [ChildFilterTypes.Course]: 'search_name',
            [ChildFilterTypes.ExternalCourse]: 'search_name',
            [ChildFilterTypes.Participant]:
              'users_all_courses_course_search_name_or_enroll_external_courses_external_course_search_name',
            [ChildFilterTypes.Area]:
              'users_users_all_courses_course_search_name_or_users_enroll_external_courses_external_course_search_name',
            [ChildFilterTypes.LearningPath]: 'name_cont',
            [ChildFilterTypes.UsersAllCourse]: 'course_search_name',
          },
        },
        type: 'string',
      },
      {
        key: FiltersColumns.profiles,
        label: t(`${prefix}.profiles_type`),
        value: {
          children: {
            [ChildFilterTypes.Course]:
              'users_all_courses_course_courses_lms_profiles_kind',
            [ChildFilterTypes.Participant]: 'users_all_courses_mandatory_or_id',
            [ChildFilterTypes.ExternalCourse]: null,
            [ChildFilterTypes.Area]: `${parentCourse}_courses_lms_profiles_kind`,
            [ChildFilterTypes.LearningPath]: 'profiles_id',
            [ChildFilterTypes.UsersAllCourse]: 'mandatory',
          },
        },
        type: 'tagsList',
        list: [
          CourseLmsProfileType.Optional,
          CourseLmsProfileType.Required,
        ].map((value) => {
          const key = getTranslationKeyCourseLmsProfile(value)
          return {
            value: String(value),
            label: t(`${prefixFilters}.${key}`),
          }
        }),
      },
      ...filterProfiles,
      {
        key: FiltersColumns.stateAdvance,
        label: t(`${prefix}.stateAdvance`),
        value: {
          children: {
            [ChildFilterTypes.Course]: 'users_all_courses_advance',
            [ChildFilterTypes.ExternalCourse]: 'users_advance',
            [ChildFilterTypes.Participant]: [
              'users_all_courses_advance',
              (_type, { compareTo }) => ({
                operator: 'in',
                compareTo: (compareTo && ENROL_STATUS_MAP[compareTo]) || [],
                input: 'enroll_external_courses_state',
              }),
            ],
            [ChildFilterTypes.UsersAllCourse]: 'advance',
            [ChildFilterTypes.Area]: [
              'users_users_all_courses_advance',
              (_type, { compareTo }) => ({
                operator: 'in',
                compareTo: (compareTo && ENROL_STATUS_MAP[compareTo]) || [],
                input: 'users_enroll_external_courses_state',
              }),
            ],
            [ChildFilterTypes.LearningPath]: null,
          },
        },
        type: 'tagsList',
        list: [
          MonitoringStatus.Finish,
          MonitoringStatus.InProgress,
          MonitoringStatus.Pending,
        ].map((value) => ({
          value: String(value),
          label: t(
            `${prefix}.stateAdvanceList.${getTranslationKeyMonitoringStatus(
              value
            )}`
          ),
        })),
      },
      {
        key: FiltersColumns.finalState,
        label: t(`${prefix}.finalState`),
        value: {
          children: {
            [ChildFilterTypes.Course]: 'synchronous_course_enrol_final_state',
            [ChildFilterTypes.Participant]:
              'enrols_synchronous_course_enrol_final_state_or_enroll_external_courses_state',
            [ChildFilterTypes.Area]: `${parentCourse}_synchronous_course_enrol_final_state`,
            [ChildFilterTypes.LearningPath]: null,
            [ChildFilterTypes.UsersAllCourse]:
              'enrol_synchronous_course_enrol_final_state',
          },
        },
        type: 'tagsList',
        list: [
          EnrolState.Approved,
          EnrolState.Denied,
          EnrolState.Pending,
          EnrolState.Absent,
          EnrolState.ExcusedAbsence,
        ].map((value) => ({
          value: String(value),
          label: t(
            `${prefix}.finalStateList.${getTranslationEnrolStateKey(value)}`
          ),
        })),
      },
      {
        key: FiltersColumns.startDate,
        label: t(`${prefix}.startDate`),
        value: {
          children: {
            [ChildFilterTypes.Course]: 'start_date_or_created_at_formatted',
            [ChildFilterTypes.ExternalCourse]: 'start_date',
            [ChildFilterTypes.UsersAllCourse]:
              'course_start_date_or_created_at_formatted',
            [ChildFilterTypes.Participant]:
              'users_all_courses_course_start_date',
            [ChildFilterTypes.Area]: `${parentCourse}_start_date_or_${parentCourse}_created_at_formatted`,
            [ChildFilterTypes.LearningPath]: null,
          },
        },
        type: 'date',
      },
      {
        key: FiltersColumns.endDate,
        label: t(`${prefix}.endDate`),
        value: {
          children: {
            [ChildFilterTypes.Course]: 'end_date',
            [ChildFilterTypes.ExternalCourse]: 'end_date',
            [ChildFilterTypes.UsersAllCourse]: 'course_end_date',
            [ChildFilterTypes.Participant]: 'users_all_courses_course_end_date',
            [ChildFilterTypes.Area]: `${parentCourse}_end_date`,
            [ChildFilterTypes.LearningPath]: null,
          },
        },
        type: 'date',
      },
      {
        key: FiltersColumns.finish,
        label: t(`${prefix}.advance.finish`),
        value: {
          children: {
            [ChildFilterTypes.Course]: 'advance_finish',
            [ChildFilterTypes.UsersAllCourse]: null,
            [ChildFilterTypes.Participant]: 'all_courses_advance_finish',
            [ChildFilterTypes.Area]: `${parentCourse}_advance_finish`,
            [ChildFilterTypes.LearningPath]: 'monitoring_users_finish_eq',
          },
        },
        type: 'number',
      },
      {
        key: FiltersColumns.inProgress,
        label: t(`${prefix}.advance.inProgress`),
        value: {
          children: {
            [ChildFilterTypes.Course]: 'advance_in_progress',
            [ChildFilterTypes.UsersAllCourse]: null,
            [ChildFilterTypes.Participant]: 'all_courses_advance_in_progress',
            [ChildFilterTypes.Area]: `${parentCourse}_advance_in_progress`,
            [ChildFilterTypes.LearningPath]: 'monitoring_users_in_progress_eq',
          },
        },
        type: 'number',
      },
      {
        key: FiltersColumns.pending,
        label: t(`${prefix}.advance.pending`),
        value: {
          children: {
            [ChildFilterTypes.Course]: 'advance_pending',
            [ChildFilterTypes.UsersAllCourse]: null,
            [ChildFilterTypes.Participant]: 'all_courses_advance_pending',
            [ChildFilterTypes.Area]: `${parentCourse}_advance_pending`,
            [ChildFilterTypes.LearningPath]: 'monitoring_users_pending_eq',
          },
        },
        type: 'number',
      },
      ...providerFilters,
    ]
  }, [allProfilesList, parentCourse, profilesLoading, providerFilters, t])
  return filtersColumns
}
